
<template>
  <v-row>
    <v-col sm="12">{{ title }}</v-col>
    <v-col sm="8">
      <Draggable
        v-model="pageModulesList"
        class="row row--dense"
        :options="draggableOptions"
      >
        <template v-for="(module, index) in pageModulesList">
          <v-col :key="index" sm="12">
            <v-card outlined>
              <v-card-title>
                {{ module.type }}
                <v-spacer></v-spacer>
                <v-btn icon @click="edit(index)">
                  <v-icon>mdi-pen</v-icon>
                </v-btn>
                <v-btn icon @click="remove(index)">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-btn icon class="handler">
                  <v-icon>mdi-arrow-all</v-icon>
                </v-btn>
              </v-card-title>
              <v-expand-transition>
                <div v-show="editOpen[index]">
                  <v-divider></v-divider>

                  <v-card-text>
                    <v-col cols="12">
                      <h3>Translations</h3>
                    </v-col>
                    <v-col cols="12">
                      <v-tabs v-model="activeLocaleIndex" class="mb-3">
                        <v-tab v-for="locale in $tLocales" :key="locale">{{
                          locale
                        }}</v-tab>
                      </v-tabs>
                      <v-tabs-items v-model="activeLocaleIndex">
                        <v-tab-item v-for="locale in $tLocales" :key="locale">
                          <template
                            v-for="(_, dataKey) in module.data[activeLocale]"
                          >
                            <v-text-field
                              :key="dataKey"
                              :label="dataLabelMap[dataKey] || dataKey"
                              v-model="module.data[activeLocale][dataKey]"
                            ></v-text-field>
                          </template>
                        </v-tab-item>
                      </v-tabs-items>
                    </v-col>
                  </v-card-text>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>
        </template>
      </Draggable>
    </v-col>
    <v-col sm="2">
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn color="green" v-on="on" dark>
            <v-icon>mdi-plus</v-icon>
            Add module
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(moduleOption, index) in types"
            :key="index"
            @click="add(moduleOption)"
          >
            <v-list-item-title>{{ moduleOption }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { Watch, Prop, Component, Mixins } from 'vue-property-decorator'
import Draggable from 'vuedraggable'
import { TranslationHandler } from '../../mixins/translation-handler'
import { PageModule, PageModuleData, PageModuleDataAdvert, PageModuleDataChart, PageModuleDataNewsletter, PageModuleDataPlaylist, PageModuleDataSocial, PageModuleDataVideo, PageModuleType } from '../../types'

@Component({
  components: { Draggable }
})
export default class BuilderPageModulesForm extends Mixins(TranslationHandler) {
  @Prop() title!: string
  @Prop() value!: PageModule<PageModuleData>[]

  pageModulesList: PageModule<PageModuleData>[] = []

  created () {
    this.pageModulesList = this.value.map((module) => {
      if (module.type === PageModuleType.PLAYLIST) {
        const _module = module as PageModule<PageModuleDataPlaylist>

        this.$tLocales.forEach((locale) => {
          if (!_module.data[locale].playlistHeight) {
            _module.data[locale].playlistHeight = '380'
          }
        })

        return _module
      }

      return module
    })
  }

  @Watch('pageModulesList', { deep: true })
  watchModules (val: PageModule<PageModuleData>[]) {
    this.$emit('input', val)
  }

  draggableOptions = {
    handle: '.handler',
  }

  activeLocaleIndex = 0
  editOpen: { [index: number]: boolean } = {}

  types: PageModuleType[] = [
    PageModuleType.CHART,
    PageModuleType.VIDEO,
    PageModuleType.ADVERTISEMENT,
    PageModuleType.PLAYLIST,
    PageModuleType.NEWSLETTER,
    PageModuleType.SOCIAL,
  ]

  dataLabelMap = {
    videoUrl: 'VideoURL or Youtube embed code',
    spotifyId: 'Spotify link (ex: https://open.spotify.com/embed/playlist/3nYUWIVEBBvsgZegpiaAkV)',
    buttonLinkShopeName: 'Shop name for tracking (ex: "Mediamarkt", "Soundcloud" or "UMG Artist Store - Rammstein")',
    buttonLinkType: 'Tracking type (ex: "D2C - Buy", "B2C - Buy", "Streaming", "Download" or "Mixed")',
    playlistHeight: 'The playlist height (ex: "100%" or "500px"). If not set, the default height is 380px.',
    buttonSave: 'Save to your Spotify library',
  }

  get activeLocale () {
    return this.$tLocales[this.activeLocaleIndex]
  }

  edit (index: number) {
    this.$set(this.editOpen, index, !this.editOpen[index])
  }

  remove (index: number) {
    this.pageModulesList.splice(index, 1)
  }

  add (type: PageModuleType) {
    const module: PageModule<PageModuleData> = {
      type: type,
      data: {}
    }

    this.$tLocales.forEach((locale) => {
      switch (type) {
        case PageModuleType.CHART: {
          (module.data as { [k: string]: PageModuleDataChart })[locale] = {
            title: 'This is the fan playlist!',
            text: 'Listen now',
            buttonShare: 'Share',
            buttonOpen: 'Open in Spotify',
            buttonRestart: 'Restart',
          }
          break
        }
        case PageModuleType.ADVERTISEMENT: {
          (module.data as { [k: string]: PageModuleDataAdvert })[locale] = {
            title: '',
            text: '',
            imageUrl: '',
            buttonLink: '',
            button: '',
            buttonLinkShopeName: '',
            buttonLinkType: 'D2C - Buy',
          }
          break
        }
        case PageModuleType.VIDEO: {
          (module.data as { [k: string]: PageModuleDataVideo })[locale] = {
            title: '',
            videoUrl: '',
          }
          break
        }
        case PageModuleType.PLAYLIST: {
          (module.data as { [k: string]: PageModuleDataPlaylist })[locale] = {
            title: 'The songs you selected',
            buttonSave: 'Save to your Spotify library',
            buttonShare: 'Share',
            buttonOpen: 'Open in Spotify',
            playlistHeight: '380',
          }
          break
        }
        case PageModuleType.SOCIAL: {
          (module.data as { [k: string]: PageModuleDataSocial })[locale] = {
            title: '',
            youtube: '',
            facebook: '',
            twitter: '',
            instagram: '',
            tiktok: '',
          }
          break
        }
        case PageModuleType.NEWSLETTER: {
          (module.data as { [k: string]: PageModuleDataNewsletter })[locale] = {
            title: '',
            terms: '',
            button: '',
            success: '',
          }
          break
        }
        default: {
          break
        }
      }
    })

    this.pageModulesList.push(module)
  }
}
</script>

<style scoped></style>
